import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './AcceptInvitation.css';

// Configure axios with the API base URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const axiosInstance = axios.create({
  baseURL: API_URL,
});

interface InvitationInfo {
  email: string;
  firstName: string;
  lastName: string;
  invitedBy: string;
}

const AcceptInvitation: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [invitationInfo, setInvitationInfo] = useState<InvitationInfo | null>(null);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  useEffect(() => {
    validateInvitation();
  }, [token]);

  const validateInvitation = async () => {
    if (!token) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.get(`/auth/invitation/${token}`);
      setInvitationInfo(response.data as InvitationInfo);
      setLoading(false);
    } catch (err: any) {
      if (err.response?.status === 400) {
        setError(err.response.data.message || 'Invalid or expired invitation');
      } else if (err.response?.status === 404) {
        setError('Invalid invitation link');
      } else {
        setError('Failed to validate invitation');
      }
      setLoading(false);
    }
  };

  const validatePassword = (value: string): string => {
    if (value.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/\d/.test(value)) {
      return 'Password must contain at least one number';
    }
    return '';
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    const error = validatePassword(value);
    setPasswordError(error);
    
    // Also check confirm password if it has a value
    if (confirmPassword) {
      setConfirmPasswordError(value !== confirmPassword ? 'Passwords do not match' : '');
    }
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    setConfirmPasswordError(value !== password ? 'Passwords do not match' : '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields
    const pwdError = validatePassword(password);
    if (pwdError) {
      setPasswordError(pwdError);
      return;
    }
    
    if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      return;
    }
    
    if (!jobTitle.trim()) {
      setError('Job title is required');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await axiosInstance.post('/auth/accept-invitation', {
        token,
        password,
        jobTitle: jobTitle.trim(),
      });

      // Show success message and redirect to login
      navigate('/login', { 
        state: { 
          message: 'Account successfully created! Please log in with your new password.' 
        } 
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to complete registration');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="invitation-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (error && !invitationInfo) {
    return (
      <div className="invitation-container">
        <div className="invitation-header">
          <h1>ComplexLaw</h1>
          <h2>PCP Claims Management System</h2>
        </div>
        <div className="invitation-form-container">
          <div className="invitation-card">
            <h2>Invalid Invitation</h2>
            <div className="error-message">{error}</div>
            <button 
              className="invitation-button"
              onClick={() => navigate('/login')}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="invitation-container">
      <div className="invitation-header">
        <h1>ComplexLaw</h1>
        <h2>PCP Claims Management System</h2>
      </div>
      
      <div className="invitation-form-container">
        <div className="invitation-card">
          <h2>Complete Your Registration</h2>
          
          {invitationInfo && (
            <div className="invitation-info">
              <p>You've been invited to join PCP Claims Management System</p>
              <p>by <strong>{invitationInfo.invitedBy}</strong></p>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={invitationInfo?.email || ''}
                disabled
                className="disabled-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="jobTitle">Job Title *</label>
              <input
                type="text"
                id="jobTitle"
                value={jobTitle}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setJobTitle(e.target.value)}
                required
                autoFocus
                placeholder="e.g. Senior Analyst"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password *</label>
              <div className="password-input-container">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePasswordChange(e.target.value)}
                  required
                  placeholder="At least 8 characters with 1 number"
                  className={passwordError ? 'error' : ''}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              {passwordError && <span className="field-error">{passwordError}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password *</label>
              <div className="password-input-container">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleConfirmPasswordChange(e.target.value)}
                  required
                  placeholder="Re-enter your password"
                  className={confirmPasswordError ? 'error' : ''}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label="Toggle password visibility"
                >
                  {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              {confirmPasswordError && <span className="field-error">{confirmPasswordError}</span>}
            </div>

            <button
              type="submit"
              className="invitation-button"
              disabled={submitting || !!passwordError || !!confirmPasswordError}
            >
              {submitting ? 'Creating Account...' : 'Complete Registration'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitation;